/**
 * @fileoverview Hook for managing available production plans
 * Provides functionality to fetch and filter production plans for assignment
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { pb } from '../helpers/pocketbase_helper';
import { PlanCentroCostoExpanded } from '../types/collections/PlanCentroCosto';

/**
 * Filter options for plans
 */
export interface PlanFilterOptions {
  activo?: boolean;
  search?: string;
}

/**
 * Hook for managing available production plans
 * @returns Object with state and functions for plan management
 */
export const usePlanesDisponibles = () => {
  // State management
  const [planes, setPlanes] = useState<PlanCentroCostoExpanded[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [filters, setFilters] = useState<PlanFilterOptions>({
    activo: true
  });
  const [centrosCostoMap, setCentrosCostoMap] = useState<Record<string, string>>({});
  
  // Abort controller for canceling requests
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError("");
  }, []);

  /**
   * Cancel ongoing requests
   */
  const cancelRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);
  
  /**
   * Fetch centro de costo names and store them in a map
   * Esta función ya no es necesaria, pero la mantenemos por compatibilidad
   */
  const fetchCentrosCostoNames = useCallback(async () => {
    // Ya no necesitamos hacer esta consulta adicional
    // Los datos los obtenemos directamente de los datos expandidos
    return;
  }, []);

  /**
   * Set loading state
   */
  const setLoadingState = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  /**
   * Set error state
   */
  const setErrorState = useCallback((errorMessage: string) => {
    setError(errorMessage);
  }, []);

  /**
   * Update filter options
   */
  const updateFilters = useCallback((newFilters: Partial<PlanFilterOptions>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Clear search filter
   */
  const clearSearch = useCallback(() => {
    setFilters(prev => ({ ...prev, search: undefined }));
  }, []);

  /**
   * Fetch available production plans based on current filters
   */
  const fetchPlanes = useCallback(async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    try {
      setLoading(true);
      setError("");

      // Fetch centro de costo names first
      await fetchCentrosCostoNames();

      // Build filter string
      let filterString = "activo = true";
      if (filters.search && filters.search.trim()) {
        filterString += ` && nombre ~ "${filters.search.trim()}"`;
      }

      const result = await pb.collection('plan_centro_costo').getList(1, 1000, {
        filter: filterString,
        sort: "nombre",
        expand: "centro_costo_peso.centro_costo.maquina,centro_costo_peso.centro_costo.encargado,centro_costo_peso.centro_costo.proceso"
      });

      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      // Only set error if no planes were returned and there was no search filter
      if (result.items.length === 0 && !filters.search) {
        setError("No hay planes disponibles");
      } else {
        setPlanes(result.items as unknown as PlanCentroCostoExpanded[]);
        // Clear any previous error if we successfully got planes
        setError("");
      }
    } catch (err: any) {
      // Don't set error if request was aborted
      if (err.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
        return;
      }
      console.error("Error fetching planes:", err);
      setError("Error al cargar planes disponibles");
    } finally {
      // Only update loading state if request wasn't aborted
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [filters, fetchCentrosCostoNames]);

  /**
   * Search plans by name
   */
  const searchPlanes = useCallback(async (searchTerm: string) => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    try {
      setLoading(true);
      setError("");

      // Make sure we have centro de costo names
      await fetchCentrosCostoNames();

      if (!searchTerm.trim()) {
        // If search is empty, fetch all active plans
        const result = await pb.collection('plan_centro_costo').getList(1, 1000, {
          filter: "activo = true",
          sort: "nombre",
          expand: "centro_costo_peso.centro_costo.maquina,centro_costo_peso.centro_costo.encargado,centro_costo_peso.centro_costo.proceso"
        });
        
        // Check if request was aborted
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }
        
        setPlanes(result.items as unknown as PlanCentroCostoExpanded[]);
        // Clear error if we successfully got planes
        setError("");
        return;
      }

      // Search by name
      const result = await pb.collection('plan_centro_costo').getList(1, 1000, {
        filter: `activo = true && nombre ~ "${searchTerm.trim()}"`,
        sort: "nombre",
        expand: "centro_costo_peso.centro_costo.maquina,centro_costo_peso.centro_costo.encargado,centro_costo_peso.centro_costo.proceso"
      });

      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      setPlanes(result.items as unknown as PlanCentroCostoExpanded[]);
      // Clear error if we successfully got planes
      setError("");
    } catch (err: any) {
      // Don't set error if request was aborted
      if (err.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
        return;
      }
      console.error("Error searching planes:", err);
      setError("Error al buscar planes");
    } finally {
      // Only update loading state if request wasn't aborted
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [fetchCentrosCostoNames]);

  /**
   * Get plan by ID with expanded data
   */
  const getPlanById = useCallback(async (planId: string): Promise<PlanCentroCostoExpanded | null> => {
    try {
      // Make sure we have centro de costo names
      await fetchCentrosCostoNames();
      
      const plan = await pb.collection('plan_centro_costo').getOne(planId, {
        expand: "centro_costo_peso.centro_costo.maquina,centro_costo_peso.centro_costo.encargado,centro_costo_peso.centro_costo.proceso"
      });
      

      
      return plan as PlanCentroCostoExpanded;
    } catch (err: any) {
      console.error("Error getting plan by ID:", err);
      return null;
    }
  }, [fetchCentrosCostoNames]);

  /**
   * Refresh plans data
   */
  const refreshPlanes = useCallback(async () => {
    await fetchPlanes();
  }, [fetchPlanes]);

  /**
   * Get plans count
   */
  const getPlanesCount = useCallback(() => {
    return planes.length;
  }, [planes]);

  /**
   * Get active plans count
   */
  const getActivePlanesCount = useCallback(() => {
    return planes.filter(plan => plan.activo).length;
  }, [planes]);

  /**
   * Check if a plan is available for assignment
   */
  const isPlanAvailable = useCallback((planId: string): boolean => {
    const plan = planes.find(p => p.id === planId);
    return plan ? plan.activo : false;
  }, [planes]);
  
  /**
   * Get centro de costo name by ID
   */
  const getCentroCostoName = useCallback((centroCostoId: string): string => {
    if (!centroCostoId) {
      return 'Centro no especificado';
    }
    
    const nombre = centrosCostoMap[centroCostoId];
    
    if (nombre) {
      return nombre;
    }
    
    // Si no encontramos el nombre en el mapa, intentamos obtener una versión corta del ID
    try {
      const shortId = centroCostoId.substring(0, 4);

      return `Centro ${shortId}`;
    } catch (e) {
      console.error(`Error obteniendo nombre para centro de costo ${centroCostoId}:`, e);
      return `Centro desconocido`;
    }
  }, [centrosCostoMap]);

  /**
   * Get plans by cost center
   */
  const getPlanesByCostCenter = useCallback((costCenterId: string): PlanCentroCostoExpanded[] => {
    return planes.filter(plan => 
      plan.centro_costo_peso && 
      plan.centro_costo_peso.some(centroCostoPeso => {
        // Si es un objeto con expand.centro_costo
        if (typeof centroCostoPeso === 'object' && centroCostoPeso?.expand?.centro_costo) {
          return centroCostoPeso.expand.centro_costo.id === costCenterId;
        }
        
        // Si es un objeto con centro_costo como ID
        if (typeof centroCostoPeso === 'object' && centroCostoPeso?.centro_costo) {
          return String(centroCostoPeso.centro_costo) === costCenterId;
        }
        
        // Si es directamente un ID (caso antiguo)
        return typeof centroCostoPeso === 'string' && centroCostoPeso === costCenterId;
      }) &&
      plan.activo
    );
  }, [planes]);

  // Auto-fetch planes when filters change (only in production)
  useEffect(() => {
    // Skip auto-fetch in test environment
    if (process.env.NODE_ENV !== 'test') {
      fetchPlanes();
    }
  }, [fetchPlanes]);

  // Cleanup function to cancel ongoing requests when component unmounts
  useEffect(() => {
    return () => {
      cancelRequests();
    };
  }, [cancelRequests]);

  return {
    // State
    planes,
    loading,
    error,
    filters,
    centrosCostoMap,
    
    // Actions
    fetchPlanes,
    searchPlanes,
    getPlanById,
    refreshPlanes,
    updateFilters,
    clearSearch,
    
    // Utilities
    getPlanesCount,
    getActivePlanesCount,
    isPlanAvailable,
    getPlanesByCostCenter,
    getCentroCostoName,
    
    // State setters
    clearError,
    setLoading: setLoadingState,
    setError: setErrorState,
    
    // Request management
    cancelRequests
  };
};
